import type { Edge, Node } from 'reactflow'
import dagre from 'dagre'

export const getLayoutedElements = (
  nodes: Node[],
  edges: Edge[],
  direction = 'TB'
) => {
  const isHorizontal = direction === 'LR'
  // Use a fresh graph every call. A module-level singleton accumulates state
  // across calls — setNode on an existing id does NOT update dagre's internal
  // ordering index, so any reorder we do below would be silently ignored on
  // the second layout of the same workflow.
  const dagreGraph = new dagre.graphlib.Graph()
  dagreGraph.setDefaultEdgeLabel(() => ({}))
  dagreGraph.setGraph({ rankdir: direction })

  // For each logic node with both yes and no branches, force the yes-target to
  // be added to dagre BEFORE the no-target. Dagre uses setNode insertion order
  // as a tie-breaker when ordering nodes within a rank, and that order overrides
  // the yes-first edge sort below. Node insertion order is normally driven by
  // backend DB id (creation time), so whichever child was created first ends up
  // on the LEFT — causing crossing whenever the no-target happens to be older.
  const reorderedNodes = [...nodes]
  edges.forEach(yesEdge => {
    if (yesEdge.sourceHandle !== 'yes') {
      return
    }
    const noEdge = edges.find(
      e => e.source === yesEdge.source && e.sourceHandle === 'no'
    )
    if (!noEdge) {
      return
    }
    const yesIdx = reorderedNodes.findIndex(n => n.id === yesEdge.target)
    const noIdx = reorderedNodes.findIndex(n => n.id === noEdge.target)
    if (yesIdx === -1 || noIdx === -1 || yesIdx < noIdx) {
      return
    }
    // yes-target appears after no-target → move it to just before no-target
    const [yesNode] = reorderedNodes.splice(yesIdx, 1)
    const newNoIdx = reorderedNodes.findIndex(n => n.id === noEdge.target)
    reorderedNodes.splice(newNoIdx, 0, yesNode)
  })

  reorderedNodes.forEach(node => {
    dagreGraph.setNode(node.id, { width: node.width, height: node.height })
  })

  // Order edges yes-first so dagre's barycenter places yes-target on the left,
  // matching the LogicEventFilterNode's hardcoded yes-handle (left) / no-handle (right).
  // Backend persists `to_node` as a Go map → JSON serialized alphabetically → "no" before "yes",
  // which without this sort makes dagre put no-target on the left and causes edges to cross.
  // This sort is presentation-only: we feed a sorted copy to dagre and return `edges` untouched.
  const sortedForLayout = [...edges].sort(
    (a, b) =>
      (a.sourceHandle === 'yes' ? 0 : 1) - (b.sourceHandle === 'yes' ? 0 : 1)
  )

  sortedForLayout.forEach(edge => {
    dagreGraph.setEdge(edge.source, edge.target)
  })

  dagre.layout(dagreGraph)

  return {
    nodes: nodes.map(node => {
      const nodeWithPosition = dagreGraph.node(node.id)
      return {
        ...node,
        targetPosition: isHorizontal ? 'left' : 'top',
        sourcePosition: isHorizontal ? 'right' : 'bottom',
        // unfortunately we need this little hack to pass a slightly different position
        // to notify react flow about the change. Moreover we are shifting the dagre node position
        // (anchor=center center) to the top left so it matches the react flow node anchor point (top left).
        position: {
          x: nodeWithPosition.x - (node.width || 0) / 2 + Math.random() / 1000,
          y: nodeWithPosition.y - (node.height || 0) / 2,
        },
      }
    }) as Node[],
    edges,
  }
}
